import {
  APICallState,
  MFAActions,
  MFAChannel,
  Selectors,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {faEnvelope} from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import {faMobile} from '@fortawesome/pro-solid-svg-icons/faMobile';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {StatusType} from 'models/StatusTypes';
import {Link, Text} from 'native-base';
import {useEffect} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {
  isAbsoluteUrl,
  maskEmail,
  maskPhoneNumber,
  redirectWithForwardUrl,
} from 'utils/utils';

import ErrorPanel from 'components/ErrorPanel';
import PanelContainer from 'components/PanelContainer';
import BodyText from 'components/PanelContainer/BodyText';
import StatusDisplay from 'components/StatusDisplay';

export interface ConfirmationSuccessProps {
  forwardUrl?: string;
  securityUrl?: string;
  autoNavigate: boolean;
}

const ConfirmationSuccess: React.FunctionComponent<ConfirmationSuccessProps> = (
  props,
) => {
  const {
    forwardUrl = '/rewards/profile',
    securityUrl = '/rewards/profile',
    autoNavigate = false,
  } = props;
  const {state} = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWebView = useWidgetState(Selectors.isWebViewSelector);
  const editOptinState = useWidgetState(Selectors.editOptinStateSelector);
  const isOptinSuccess = editOptinState === APICallState.SUCCESS;
  const dispatch = useWidgetDispatch();
  const email =
    (useWidgetState(Selectors.securityEmailSelector) as string | undefined) ??
    '';
  const phoneNumber =
    (useWidgetState(Selectors.securityMobilePhoneSelector) as
      | string
      | undefined) ?? '';
  const selectedChannel = useWidgetState(Selectors.selectedChannelSelector);
  const confirmationType = state ?? selectedChannel;
  const optinStatus = useWidgetState(Selectors.mfaOptinStatusSelector) as
    | boolean
    | undefined;
  const isOptedin = optinStatus || isOptinSuccess;

  useEffect(() => {
    if (!optinStatus) {
      dispatch(MFAActions.editOptinStatus(true));
    }
  }, [dispatch, optinStatus]);

  useEffect(() => {
    if (autoNavigate && isOptedin) {
      setTimeout(() => {
        if (!isAbsoluteUrl(forwardUrl)) {
          redirectWithForwardUrl(forwardUrl);
        }
      }, 5000);
    }
  }, [autoNavigate, forwardUrl, isOptedin]);

  const onContinuePress = () => {
    //console.log(forwardUrl);
  };

  const legalDescription = (
    <BodyText fontSize="12px">
      To update your verification method, visit{' '}
      <Link
        _hover={{
          textDecoration: 'underline',
        }}
        href={securityUrl}
        display="inline"
      >
        <BodyText fontSize="12px" bold>
          Security
        </BodyText>
      </Link>{' '}
      within My Account
    </BodyText>
  );

  if (isWebView && isOptedin) {
    if (!searchParams.get('optin')) {
      const params = Object.fromEntries(searchParams);
      setSearchParams({...params, optin: 'true'});
      return null;
    }
  }

  if (editOptinState === APICallState.ERROR) {
    return (
      <ErrorPanel
        onRetryPress={() => dispatch(MFAActions.editOptinStatus(true))}
        onCancelPress={undefined}
        retryInProgress={false} //FIXME: pass retry state
      />
    );
  }

  return (
    <PanelContainer
      primaryTitle="View My Account"
      textLink={'This page will redirect automatically or: '}
      onPrimaryPress={onContinuePress}
      isDialogBox={false}
      preserveButtonOrderMobile={true}
      legalCopyText={legalDescription}
    >
      <StatusDisplay titleText="SUCCESS" statusType={StatusType.SUCCESS}>
        <>
          <Text mb="24px" bold>
            Two-Step Verification is on.
          </Text>

          {confirmationType === MFAChannel.EMAIL ? (
            <>
              <Text pb="6px">We’ll send email verification links to:</Text>
              <Text>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  color="#910B25"
                  style={{
                    position: 'relative',
                    fontSize: 16,
                    marginRight: '8px',
                    paddingTop: '4px',
                    top: '2px',
                  }}
                />
                Email: {maskEmail(email)}
              </Text>
            </>
          ) : (
            <>
              <Text pb="6px">We’ll send SMS verification codes to:</Text>
              <Text>
                <FontAwesomeIcon
                  icon={faMobile}
                  color="#910B25"
                  style={{
                    position: 'relative',
                    fontSize: 16,
                    marginRight: '8px',
                    paddingTop: '4px',
                    top: '2px',
                  }}
                />
                Mobile: {maskPhoneNumber(phoneNumber)}
              </Text>
            </>
          )}
        </>
      </StatusDisplay>
    </PanelContainer>
  );
};

export default ConfirmationSuccess;
